import React, { useMemo } from 'react';
import { ReactFlow, MiniMap, Background, BackgroundVariant } from '@xyflow/react';
import type { Node, Edge } from '@xyflow/react';
import type { PlaybookDiff } from '../../types/playbook.types';
import StatusNode from './StatusNode';
import TransitionEdge from './TransitionEdge';

interface CanvasDiffOverlayProps {
  nodes: Node[];
  edges: Edge[];
  diff: PlaybookDiff;
}

const nodeTypes = { statusNode: StatusNode };
const edgeTypes = { transitionEdge: TransitionEdge };

const CanvasDiffOverlay: React.FC<CanvasDiffOverlayProps> = ({ nodes, edges, diff }) => {
  const statusSlugs = useMemo(() => {
    const map = new Map<string, 'added' | 'removed' | 'modified'>();
    diff.statusChanges.forEach((sc) => map.set(sc.slug, sc.changeType));
    return map;
  }, [diff]);

  const transitionSlugs = useMemo(() => {
    const map = new Map<string, 'added' | 'removed' | 'modified'>();
    diff.transitionChanges.forEach((tc) => map.set(tc.slug, tc.changeType));
    return map;
  }, [diff]);

  // Decorate nodes with diff styling
  const diffNodes = useMemo(() => nodes.map((n) => {
    const data = n.data as any;
    const change = statusSlugs.get(data?.slug);
    if (!change) return n;

    const borderClass =
      change === 'added' ? '2px dashed #10b981' :
      change === 'removed' ? '2px dashed #f43f5e' :
      '2px solid #f59e0b';

    const opacity = change === 'removed' ? 0.5 : 1;

    return {
      ...n,
      style: {
        ...n.style,
        border: borderClass,
        opacity,
      },
    };
  }), [nodes, statusSlugs]);

  // Decorate edges with diff styling
  const diffEdges = useMemo(() => edges.map((e) => {
    const data = e.data as any;
    const change = transitionSlugs.get(data?.slug);
    if (!change) return e;

    const stroke =
      change === 'added' ? '#10b981' :
      change === 'removed' ? '#f43f5e' :
      '#f59e0b';

    const strokeDasharray = change === 'removed' || change === 'added' ? '6 3' : undefined;
    const opacity = change === 'removed' ? 0.5 : 1;

    return {
      ...e,
      style: {
        ...e.style,
        stroke,
        strokeDasharray,
        opacity,
        strokeWidth: 2,
      },
    };
  }), [edges, transitionSlugs]);

  // Ghost nodes for removed statuses
  const removedNodes = useMemo(() => {
    return diff.statusChanges
      .filter((sc) => sc.changeType === 'removed')
      .map((sc, i) => ({
        id: `ghost-${sc.slug}`,
        type: 'statusNode',
        position: { x: 400 + i * 220, y: 50 },
        data: {
          label: sc.slug,
          slug: sc.slug,
          category: 'todo' as const,
          statusId: `ghost-${sc.slug}`,
        },
        style: {
          border: '2px dashed #f43f5e',
          opacity: 0.4,
        },
      }));
  }, [diff]);

  return (
    <div className="h-full w-full relative">
      {/* Diff legend */}
      <div className="absolute top-2 left-2 z-10 flex items-center gap-3 rounded-md bg-white/90 dark:bg-gray-800/90 px-3 py-1.5 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1">
          <span className="h-2 w-4 border-2 border-dashed border-emerald-500 rounded-sm" />
          <span className="text-[9px] text-gray-500">Added</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="h-2 w-4 border-2 border-dashed border-rose-500 rounded-sm opacity-50" />
          <span className="text-[9px] text-gray-500">Removed</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="h-2 w-4 border-2 border-solid border-amber-500 rounded-sm" />
          <span className="text-[9px] text-gray-500">Modified</span>
        </div>
      </div>

      <ReactFlow
        nodes={[...diffNodes, ...removedNodes]}
        edges={diffEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={1.5}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e5e7eb" />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="!bg-gray-50 dark:!bg-gray-900 !border-gray-200 dark:!border-gray-700"
        />
      </ReactFlow>
    </div>
  );
};

export default CanvasDiffOverlay;
